import { FormEvent, useState, useRef } from 'react';
import { WineFormData, Wine, CellarSuggestions } from '../types';
import { AutocompleteInput } from './AutocompleteInput';

interface CriticScoreFound {
  critic_name: string;
  score: number;
}

interface ScanResults {
  scores: CriticScoreFound[];
  notes?: string;
}

interface WineFormProps {
  wineForm: WineFormData;
  setWineForm: (form: WineFormData) => void;
  editingWine: Wine | null;
  onSubmit: (e: FormEvent, scanResults?: ScanResults) => void;
  onDelete?: () => void;
  onClose: () => void;
  suggestions: CellarSuggestions;
}

// Compress and resize image to reduce API costs
async function compressImage(
  file: File,
  maxSize: number = 1200,
  quality: number = 0.8
): Promise<{ base64: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let width = img.width;
      let height = img.height;

      if (width > height && width > maxSize) {
        height = (height * maxSize) / width;
        width = maxSize;
      } else if (height > maxSize) {
        width = (width * maxSize) / height;
        height = maxSize;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      const base64 = dataUrl.split(',')[1];

      resolve({ base64, mimeType: 'image/jpeg' });
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

export function WineForm({ 
  wineForm, 
  setWineForm, 
  editingWine, 
  onSubmit, 
  onDelete,
  onClose,
  suggestions 
}: WineFormProps) {
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [foundScores, setFoundScores] = useState<CriticScoreFound[]>([]);
  const [foundCriticNotes, setFoundCriticNotes] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleScanLabel = async (file: File) => {
    setScanning(true);
    setScanError(null);
    setFoundScores([]);
    setFoundCriticNotes(null);
    
    try {
      const { base64, mimeType } = await compressImage(file);
      
      const response = await fetch('/.netlify/functions/scan-label', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64, mimeType })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to scan label');
      }
      
      const data = await response.json();
      
      // Log scan results for debugging
      console.log('[Scan] Full response:', data);
      console.log('[Scan] Label:', data.label);
      console.log('[Scan] Vivino:', data.vivino);
      console.log('[Scan] Critic scores:', data.critic_scores);
      console.log('[Scan] Debug:', data.debug);
      
      // Update form with extracted data - batch all changes
      const updates: Partial<WineFormData> = {};
      const label = data.label || {};
      if (label.wine_name) updates.wine_name = label.wine_name;
      if (label.producer) updates.producer = label.producer;
      if (label.vintage) updates.vintage = String(label.vintage);
      if (label.varietal) updates.varietal = label.varietal;
      if (label.country) updates.country = label.country;
      if (label.region) updates.region = label.region;
      if (label.appellation) updates.appellation = label.appellation;
      if (label.color) updates.color = label.color;
      if (label.wine_type) updates.wine_type = label.wine_type;
      
      if (Object.keys(updates).length > 0) {
        setWineForm({ ...wineForm, ...updates });
      }
      
      // Collect all scores (from critic search AND Vivino API)
      const allScores: CriticScoreFound[] = [];
      
      // Add Vivino score if found via direct API
      if (data.vivino?.vivino_rating) {
        allScores.push({
          critic_name: 'Vivino',
          score: data.vivino.vivino_rating
        });
      }
      
      // Add critic scores from web search
      if (data.critic_scores && Array.isArray(data.critic_scores)) {
        // Filter out Vivino if we already have it from direct API
        const hasVivino = allScores.some(s => s.critic_name === 'Vivino');
        data.critic_scores.forEach((score: CriticScoreFound) => {
          if (hasVivino && score.critic_name === 'Vivino') return;
          allScores.push(score);
        });
      }
      
      if (allScores.length > 0) {
        setFoundScores(allScores);
      }
      
      // Store found critic notes
      if (data.critic_notes) {
        setFoundCriticNotes(data.critic_notes);
      }
      
    } catch (error) {
      console.error('Scan error:', error);
      setScanError(error instanceof Error ? error.message : 'Failed to scan label');
    } finally {
      setScanning(false);
    }
  };

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    const scanResults: ScanResults | undefined = (foundScores.length > 0 || foundCriticNotes) 
      ? { scores: foundScores, notes: foundCriticNotes || undefined }
      : undefined;
    onSubmit(e, scanResults);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleScanLabel(file);
    }
    e.target.value = '';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="ios-sheet w-full"
        style={{ height: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="ios-sheet-handle" />
        
        {/* Header */}
        <div style={{ 
          padding: '0 20px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-wine)',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <h2 className="wine-title" style={{ 
            fontSize: '18px', 
            margin: 0,
            color: 'var(--color-text)'
          }}>
            {editingWine ? 'Edit Wine' : 'Add Wine'}
          </h2>
          <div style={{ width: '50px' }} />
        </div>

        {/* Scrollable Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
          {/* Scan Label Button */}
          {!editingWine && (
            <div style={{ marginBottom: '16px' }}>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                ref={fileInputRef}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={scanning}
                className="ios-btn w-full"
                style={{ 
                  background: scanning 
                    ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' 
                    : 'linear-gradient(135deg, var(--color-wine) 0%, var(--color-wine-light) 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {scanning && <div className="scanning-overlay" />}
                {scanning ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span className="animate-spin" style={{ fontSize: '18px' }}>🍷</span>
                    <span>Analyzing Label...</span>
                  </span>
                ) : (
                  <>📷 Scan Wine Label</>
                )}
              </button>
              
              {/* Scanning status card */}
              {scanning && (
                <div className="ios-card animate-fade-in" style={{ marginTop: '12px', padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div className="animate-pulse" style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '8px',
                      background: 'var(--color-wine)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '24px'
                    }}>
                      🍷
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontWeight: '600', color: 'var(--color-text)', marginBottom: '4px' }}>
                        Scanning wine label...
                      </p>
                      <p style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                        Looking up ratings & tasting notes
                      </p>
                    </div>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-bar-fill animate-pulse" style={{ width: '60%' }} />
                  </div>
                </div>
              )}
              
              {scanError && (
                <p style={{ color: 'var(--color-danger)', fontSize: '14px', marginTop: '8px' }}>{scanError}</p>
              )}
              {!scanning && (foundScores.length > 0 || foundCriticNotes) && (
                <div className="ios-card animate-fade-in" style={{ marginTop: '8px', padding: '12px' }}>
                  {foundScores.length > 0 && (
                    <div style={{ fontSize: '14px' }}>
                      <span style={{ color: 'var(--color-wine)' }}>Found Ratings: </span>
                      {foundScores.map((score, i) => (
                        <span key={score.critic_name}>
                          {i > 0 && ' · '}
                          <strong>{score.critic_name}:</strong> {score.critic_name === 'Vivino' ? score.score.toFixed(1) : score.score}
                        </span>
                      ))}
                    </div>
                  )}
                  {foundCriticNotes && (
                    <div style={{ fontStyle: 'italic', fontSize: '12px', color: 'var(--color-text)', marginTop: '4px' }}>
                      "{foundCriticNotes}"
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleFormSubmit}>
            {/* Core Wine Info */}
            <p className="form-section-title">Wine Details</p>
            
            <div style={{ marginBottom: '12px' }}>
              <label className="ios-label">Wine Name (optional)</label>
              <input
                type="text"
                value={wineForm.wine_name}
                onChange={(e) => setWineForm({ ...wineForm, wine_name: e.target.value })}
                className="ios-input"
                placeholder="e.g., Opus One, Reserve"
              />
              <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                Leave blank if wine is identified by producer + varietal
              </p>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label className="ios-label">Producer / Winery *</label>
              <AutocompleteInput
                value={wineForm.producer}
                onChange={(val) => setWineForm({ ...wineForm, producer: val })}
                suggestions={suggestions.producers}
                className="ios-input"
                placeholder="e.g., Robert Mondavi"
                required
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="ios-label">Vintage</label>
                <input
                  type="number"
                  value={wineForm.vintage}
                  onChange={(e) => setWineForm({ ...wineForm, vintage: e.target.value })}
                  className="ios-input"
                  min="1900"
                  max="2030"
                  placeholder="e.g., 2019"
                />
              </div>
              <div>
                <label className="ios-label">Quantity *</label>
                <input
                  type="number"
                  value={wineForm.quantity}
                  onChange={(e) => setWineForm({ ...wineForm, quantity: parseInt(e.target.value) || 1 })}
                  className="ios-input"
                  min="1"
                  required
                />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="ios-label">Color *</label>
                <select
                  value={wineForm.color}
                  onChange={(e) => setWineForm({ ...wineForm, color: e.target.value as WineFormData['color'] })}
                  className="ios-input"
                >
                  <option value="red">Red</option>
                  <option value="white">White</option>
                  <option value="rosé">Rosé</option>
                  <option value="orange">Orange</option>
                </select>
              </div>
              <div>
                <label className="ios-label">Type *</label>
                <select
                  value={wineForm.wine_type}
                  onChange={(e) => setWineForm({ ...wineForm, wine_type: e.target.value as WineFormData['wine_type'] })}
                  className="ios-input"
                >
                  <option value="still">Still</option>
                  <option value="sparkling">Sparkling</option>
                  <option value="semi-sparkling">Semi-Sparkling</option>
                  <option value="fortified">Fortified</option>
                  <option value="dessert">Dessert</option>
                </select>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label className="ios-label">Varietal / Grape</label>
              <AutocompleteInput
                value={wineForm.varietal}
                onChange={(val) => setWineForm({ ...wineForm, varietal: val })}
                suggestions={suggestions.varietals}
                className="ios-input"
                placeholder="e.g., Cabernet Sauvignon"
              />
            </div>

            {/* Origin */}
            <p className="form-section-title">Origin</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="ios-label">Country</label>
                <AutocompleteInput
                  value={wineForm.country}
                  onChange={(val) => setWineForm({ ...wineForm, country: val })}
                  suggestions={suggestions.countries}
                  className="ios-input"
                  placeholder="e.g., France"
                />
              </div>
              <div>
                <label className="ios-label">Region</label>
                <AutocompleteInput
                  value={wineForm.region}
                  onChange={(val) => setWineForm({ ...wineForm, region: val })}
                  suggestions={suggestions.regions}
                  className="ios-input"
                  placeholder="e.g., Bordeaux"
                />
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label className="ios-label">Appellation</label>
              <AutocompleteInput
                value={wineForm.appellation}
                onChange={(val) => setWineForm({ ...wineForm, appellation: val })}
                suggestions={suggestions.appellations}
                className="ios-input"
                placeholder="e.g., Pauillac"
              />
            </div>

            {/* Storage & Value */}
            <p className="form-section-title">Storage & Value</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="ios-label">Bottle Size</label>
                <select
                  value={wineForm.bottle_size}
                  onChange={(e) => setWineForm({ ...wineForm, bottle_size: e.target.value })}
                  className="ios-input"
                >
                  <option value="375ml">375ml (Half)</option>
                  <option value="750ml">750ml (Standard)</option>
                  <option value="1.5L">1.5L (Magnum)</option>
                  <option value="3L">3L (Double Mag)</option>
                </select>
              </div>
              <div>
                <label className="ios-label">Price ($)</label>
                <input
                  type="number"
                  value={wineForm.purchase_price}
                  onChange={(e) => setWineForm({ ...wineForm, purchase_price: e.target.value })}
                  className="ios-input"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label className="ios-label">Cellar Location</label>
              <AutocompleteInput
                value={wineForm.cellar_location}
                onChange={(val) => setWineForm({ ...wineForm, cellar_location: val })}
                suggestions={suggestions.locations}
                className="ios-input"
                placeholder="e.g., Rack A, Bin 12"
              />
            </div>

            {/* Rating */}
            <p className="form-section-title">Rating</p>
            
            <div style={{ marginBottom: '12px' }}>
              <label className="ios-label">Your Rating (1-100)</label>
              <input
                type="number"
                value={wineForm.my_rating}
                onChange={(e) => setWineForm({ ...wineForm, my_rating: e.target.value })}
                className="ios-input"
                min="1"
                max="100"
                placeholder="Optional"
              />
            </div>

            {/* Notes */}
            <p className="form-section-title">Notes</p>
            
            <div style={{ marginBottom: '16px' }}>
              <label className="ios-label">Tasting Notes</label>
              <textarea
                value={wineForm.tasting_notes}
                onChange={(e) => setWineForm({ ...wineForm, tasting_notes: e.target.value })}
                className="ios-input"
                style={{ resize: 'none' }}
                rows={3}
                placeholder="Your notes about this wine..."
              />
            </div>
          </form>
        </div>

        {/* Fixed Submit Button */}
        <div style={{ 
          padding: '16px 20px', 
          borderTop: '1px solid var(--color-border)',
          background: 'var(--color-card)'
        }}>
          <button
            type="submit"
            onClick={handleFormSubmit}
            className="ios-btn ios-btn-primary w-full"
            style={{ fontSize: '17px', marginBottom: editingWine && onDelete ? '8px' : '0' }}
          >
            {editingWine ? 'Save Changes' : 'Add Wine'}
          </button>
          {editingWine && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="ios-btn w-full"
              style={{ background: '#fee2e2', color: '#991b1b' }}
            >
              Delete Wine
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
